/* eslint-disable object-property-newline */
/**
 * Crate calculator for edge quotes.
 * EMC: modular sections → crate groups. Fabricated: single fixed piece (no auto-split).
 */

const CRATING_WEIGHT_PER_LF = 22.5
const CRATE_WIDTH_IN = 27
const CRATE_WIDTH_FT = CRATE_WIDTH_IN / 12
const MAX_CRATE_LENGTH_FT = 10
const MAX_CRATE_HEIGHT_FT = 7
const LENGTH_PADDING_FT = 1
const HEIGHT_PADDING_FT = 1
// Modular pieces (EMC, pole cover) ship as equal sections when either dimension exceeds these limits.
const MODULAR_MAX_SECTION_LENGTH_FT = 10
const MODULAR_MAX_SECTION_HEIGHT_FT = 6

const EMC_SF_LB_PER_SF = 9.46
const EMC_DF_LB_PER_SF = 18.92
const CABINET_LB_PER_SF = 5.6
const POLE_COVER_LB_PER_SF = 3.41
const POLE_COVER_DEPTH_FT = 1
const DURACORE_LB_PER_SF = 3.0

const FREIGHT_CLASS_TABLE = [
  [0, 500], [1, 400], [2, 300], [3, 250], [4, 200],
  [5, 175], [6, 150], [7, 125], [8, 110], [9, 100],
  [10.5, 92.5], [12, 85], [13.5, 77.5], [15, 70],
  [22.5, 65], [30, 60], [35, 55], [50, 50]
]

function round2 (n) { return Math.round(n * 100) / 100 }

function vlookupDensityToClass (density) {
  if (density == null || isNaN(density) || density <= 0) return null
  let result = FREIGHT_CLASS_TABLE[0][1]
  for (const [threshold, cls] of FREIGHT_CLASS_TABLE) {
    if (threshold <= density) result = cls
    else break
  }
  return result
}

/** Split a dimension into equal sections, each at most maxChunkFt. */
export function splitDimension (totalFt, maxChunkFt) {
  if (totalFt <= 0) return []
  if (totalFt <= maxChunkFt) return [totalFt]
  const count = Math.ceil(totalFt / maxChunkFt)
  const size = totalFt / count
  return Array.from({ length: count }, () => size)
}

export function computeModularSectionGrid (lengthFt, heightFt) {
  const heightNeedsSplit = heightFt > MODULAR_MAX_SECTION_HEIGHT_FT
  const lengthNeedsSplit = lengthFt > MODULAR_MAX_SECTION_LENGTH_FT

  if (heightNeedsSplit && !lengthNeedsSplit) {
    const heightParts = splitDimension(heightFt, MODULAR_MAX_SECTION_HEIGHT_FT)
    return heightParts.map((h, gridIndex) => ({ lengthFt, heightFt: h, gridIndex }))
  }

  if (lengthNeedsSplit && !heightNeedsSplit) {
    const lengthParts = splitDimension(lengthFt, MODULAR_MAX_SECTION_LENGTH_FT)
    return lengthParts.map((l, gridIndex) => ({ lengthFt: l, heightFt, gridIndex }))
  }

  if (lengthNeedsSplit && heightNeedsSplit) {
    const lengthParts = splitDimension(lengthFt, MODULAR_MAX_SECTION_LENGTH_FT)
    const heightParts = splitDimension(heightFt, MODULAR_MAX_SECTION_HEIGHT_FT)
    const grid = []
    let gridIndex = 0
    for (const h of heightParts) {
      for (const l of lengthParts) {
        grid.push({ lengthFt: l, heightFt: h, gridIndex: gridIndex++ })
      }
    }
    return grid
  }

  return [{ lengthFt, heightFt, gridIndex: 0 }]
}

/** @deprecated use computeModularSectionGrid */
export function computeEmcSectionGrid (lengthFt, heightFt) {
  return computeModularSectionGrid(lengthFt, heightFt)
}

function computeWeight (type, dims) {
  const { heightFt = 0, lengthFt = 0, depthFt = 0, face } = dims
  switch (type) {
    case 'emc':
      return heightFt * lengthFt * (face === 'Single' ? EMC_SF_LB_PER_SF : EMC_DF_LB_PER_SF)
    case 'cabinet':
      return heightFt * lengthFt * CABINET_LB_PER_SF
    case 'poleCover': {
      const d = depthFt || POLE_COVER_DEPTH_FT
      const shellArea = 2 * (heightFt * lengthFt + heightFt * d + lengthFt * d)
      return shellArea * POLE_COVER_LB_PER_SF
    }
    case 'duracore':
      return heightFt * lengthFt * DURACORE_LB_PER_SF
    default:
      return 0
  }
}

function pieceFitsStandardCrate (lengthFt, heightFt) {
  return (lengthFt + LENGTH_PADDING_FT) <= MAX_CRATE_LENGTH_FT &&
    (heightFt + HEIGHT_PADDING_FT) <= MAX_CRATE_HEIGHT_FT
}

function buildCrate (number, components, signHeightFt, options = {}) {
  const { requiresEngineeredSplit = false } = options
  const maxLength = Math.max(...components.map(c => c.lengthFt))
  const rawLength = maxLength + LENGTH_PADDING_FT
  const rawHeight = signHeightFt + HEIGHT_PADDING_FT
  const lengthFt = Math.min(rawLength, MAX_CRATE_LENGTH_FT)
  const heightFt = Math.min(rawHeight, MAX_CRATE_HEIGHT_FT)
  const oversized = requiresEngineeredSplit ||
    rawLength > MAX_CRATE_LENGTH_FT ||
    rawHeight > MAX_CRATE_HEIGHT_FT

  const cratingWeightLb = lengthFt * CRATING_WEIGHT_PER_LF
  const productWeightLb = components.reduce((s, c) => s + c.weight, 0)
  const totalWeightLb = cratingWeightLb + productWeightLb
  const volume = lengthFt * CRATE_WIDTH_FT * heightFt
  const density = volume > 0 ? totalWeightLb / volume : 0

  return {
    number,
    lengthFt: round2(lengthFt),
    widthIn: CRATE_WIDTH_IN,
    heightFt: round2(heightFt),
    oversized,
    requiresEngineeredSplit,
    cratingWeightLb: round2(cratingWeightLb),
    productWeightLb: round2(productWeightLb),
    totalWeightLb: round2(totalWeightLb),
    density: round2(density),
    freightClass: vlookupDensityToClass(density),
    contents: components.map(c => c.label)
  }
}

/** Build EMC modular sections with weights. */
export function computeEmcSections (emc) {
  const grid = computeModularSectionGrid(emc.lengthFt, emc.heightFt)
  const isDouble = emc.face === 'Double'
  const totalWeight = computeWeight('emc', emc)
  const physicalSectionCount = grid.length * (isDouble ? 2 : 1)
  const weightPerSection = physicalSectionCount > 0 ? totalWeight / physicalSectionCount : 0
  const faceLabel = isDouble ? 'DF' : 'SF'

  const sections = []
  for (const cell of grid) {
    if (isDouble) {
      sections.push({
        type: 'emc',
        label: `EMC (${faceLabel}) face A §${cell.gridIndex + 1}`,
        lengthFt: cell.lengthFt,
        heightFt: cell.heightFt,
        gridIndex: cell.gridIndex,
        faceSide: 'A',
        weight: weightPerSection
      })
      sections.push({
        type: 'emc',
        label: `EMC (${faceLabel}) face B §${cell.gridIndex + 1}`,
        lengthFt: cell.lengthFt,
        heightFt: cell.heightFt,
        gridIndex: cell.gridIndex,
        faceSide: 'B',
        weight: weightPerSection
      })
    } else {
      sections.push({
        type: 'emc',
        label: `EMC (${faceLabel}) §${cell.gridIndex + 1}`,
        lengthFt: cell.lengthFt,
        heightFt: cell.heightFt,
        gridIndex: cell.gridIndex,
        weight: weightPerSection
      })
    }
  }
  return sections
}

/** Build pole cover modular sections with weights (same 6' × 10' limits as EMC). */
export function computePoleCoverSections (poleCover) {
  const grid = computeModularSectionGrid(poleCover.lengthFt, poleCover.heightFt)
  const totalWeight = computeWeight('poleCover', poleCover)
  const sectionCount = grid.length
  const weightPerSection = sectionCount > 0 ? totalWeight / sectionCount : 0

  return grid.map((cell) => ({
    type: 'poleCover',
    label: sectionCount > 1 ? `Pole Cover §${cell.gridIndex + 1}` : 'Pole Cover',
    lengthFt: cell.lengthFt,
    heightFt: cell.heightFt,
    gridIndex: cell.gridIndex,
    weight: weightPerSection
  }))
}

/** Group modular sections into crate-bound component lists (single-face / pole cover). */
export function groupModularSectionsIntoCrates (sections) {
  if (sections.length === 0) return []
  const stackHeight = Math.max(...sections.map(s => s.heightFt))
  return [{ components: [...sections], signHeightFt: stackHeight }]
}

/** Group EMC sections into crate-bound component lists. */
export function groupEmcSectionsIntoCrates (sections, isDouble) {
  if (sections.length === 0) return []

  if (!isDouble) {
    const stackHeight = Math.max(...sections.map(s => s.heightFt))
    return [{ components: [...sections], signHeightFt: stackHeight }]
  }

  const gridIndices = [...new Set(sections.map(s => s.gridIndex))].sort((a, b) => a - b)
  return gridIndices.map((gridIndex) => {
    const group = sections.filter(s => s.gridIndex === gridIndex)
    const stackHeight = Math.max(...group.map(s => s.heightFt))
    return { components: group, signHeightFt: stackHeight }
  })
}

function buildFabricatedComponent (type, label, lengthFt, heightFt, weight) {
  return { type, label, lengthFt, heightFt, weight, fabricated: true }
}

function tryCoPackFabricated (crateGroups, fabricatedComponents, arrangement) {
  const unassigned = [...fabricatedComponents]
  const groups = crateGroups.map(g => ({ ...g, components: [...g.components] }))

  for (const fab of unassigned.slice()) {
    let placed = false
    for (const group of groups) {
      const combinedHeight = arrangement === 'stacked'
        ? group.signHeightFt + fab.heightFt
        : Math.max(group.signHeightFt, fab.heightFt)
      const maxLength = Math.max(...group.components.map(c => c.lengthFt), fab.lengthFt)
      if (pieceFitsStandardCrate(maxLength, combinedHeight)) {
        group.components.push(fab)
        group.signHeightFt = combinedHeight
        unassigned.splice(unassigned.indexOf(fab), 1)
        placed = true
        break
      }
    }
    if (!placed) break
  }

  for (const fab of unassigned) {
    const needsSplit = !pieceFitsStandardCrate(fab.lengthFt, fab.heightFt)
    groups.push({
      components: [fab],
      signHeightFt: fab.heightFt,
      requiresEngineeredSplit: needsSplit
    })
  }

  return groups
}

export function calculateCrate (inputs) {
  const { emc, cabinet, poleCover, monument, arrangement = 'stacked' } = inputs

  const empty = {
    crates: [],
    components: [],
    emcSections: [],
    overallSignHeight: 0,
    totalProductWeight: 0,
    totalCratingWeight: 0,
    totalWeight: 0,
    requiresEngineeredSplit: false
  }

  const displayComponents = []
  let emcSections = []
  let crateGroups = []

  if (emc && emc.lengthFt > 0 && emc.heightFt > 0) {
    emcSections = computeEmcSections(emc)
    const isDouble = emc.face === 'Double'
    crateGroups = groupEmcSectionsIntoCrates(emcSections, isDouble)
    displayComponents.push({
      type: 'emc',
      label: `EMC (${emc.face === 'Single' ? 'SF' : 'DF'})`,
      lengthFt: Number(emc.lengthFt),
      heightFt: Number(emc.heightFt),
      weight: round2(computeWeight('emc', emc)),
      sections: emcSections.map(s => ({
        label: s.label,
        lengthFt: round2(s.lengthFt),
        heightFt: round2(s.heightFt)
      }))
    })
  }

  const fabricatedComponents = []

  if (cabinet && cabinet.lengthFt > 0 && cabinet.heightFt > 0) {
    const comp = buildFabricatedComponent(
      'cabinet',
      'ID Cabinet',
      Number(cabinet.lengthFt),
      Number(cabinet.heightFt),
      computeWeight('cabinet', cabinet)
    )
    fabricatedComponents.push(comp)
    displayComponents.push({ ...comp, weight: round2(comp.weight) })
  }

  if (poleCover && poleCover.lengthFt > 0 && poleCover.heightFt > 0) {
    const poleCoverSections = computePoleCoverSections(poleCover)
    const [poleCoverGroup] = groupModularSectionsIntoCrates(poleCoverSections)
    if (crateGroups.length === 0) {
      crateGroups = [poleCoverGroup]
    } else {
      let merged = false
      for (const existing of crateGroups) {
        const combinedHeight = arrangement === 'stacked'
          ? existing.signHeightFt + poleCoverGroup.signHeightFt
          : Math.max(existing.signHeightFt, poleCoverGroup.signHeightFt)
        const maxLength = Math.max(
          ...existing.components.map(c => c.lengthFt),
          ...poleCoverGroup.components.map(c => c.lengthFt)
        )
        if (pieceFitsStandardCrate(maxLength, combinedHeight)) {
          existing.components.push(...poleCoverGroup.components)
          existing.signHeightFt = combinedHeight
          merged = true
          break
        }
      }
      if (!merged) crateGroups.push(poleCoverGroup)
    }
    displayComponents.push({
      type: 'poleCover',
      label: 'Pole Cover',
      lengthFt: Number(poleCover.lengthFt),
      heightFt: Number(poleCover.heightFt),
      weight: round2(computeWeight('poleCover', poleCover)),
      sections: poleCoverSections.map(s => ({
        label: s.label,
        lengthFt: round2(s.lengthFt),
        heightFt: round2(s.heightFt)
      }))
    })
  }

  if (monument && monument.style === 'DuraCore' && monument.lengthFt > 0 && monument.heightFt > 0) {
    const comp = buildFabricatedComponent(
      'duracore',
      'DuraCore Monument',
      Number(monument.lengthFt),
      Number(monument.heightFt),
      computeWeight('duracore', monument)
    )
    fabricatedComponents.push(comp)
    displayComponents.push({ ...comp, weight: round2(comp.weight) })
  }

  if (displayComponents.length === 0) return empty

  if (crateGroups.length === 0 && fabricatedComponents.length > 0) {
    for (const fab of fabricatedComponents) {
      const needsSplit = !pieceFitsStandardCrate(fab.lengthFt, fab.heightFt)
      crateGroups.push({
        components: [fab],
        signHeightFt: fab.heightFt,
        requiresEngineeredSplit: needsSplit
      })
    }
  } else if (fabricatedComponents.length > 0) {
    crateGroups = tryCoPackFabricated(crateGroups, fabricatedComponents, arrangement)
  }

  const emcComp = displayComponents.find(c => c.type === 'emc')
  const cabinetComp = displayComponents.find(c => c.type === 'cabinet')
  const poleCoverComp = displayComponents.find(c => c.type === 'poleCover')
  const duracoreComp = displayComponents.find(c => c.type === 'duracore')

  let overallSignHeight
  if (duracoreComp && displayComponents.length === 1) {
    overallSignHeight = duracoreComp.heightFt
  } else if (arrangement === 'side-by-side') {
    overallSignHeight = Math.max(...[emcComp, cabinetComp].filter(Boolean).map(c => c.heightFt))
  } else {
    overallSignHeight = [emcComp, cabinetComp].filter(Boolean).reduce((h, c) => h + c.heightFt, 0)
  }
  if (poleCoverComp && poleCoverComp.heightFt > overallSignHeight) {
    overallSignHeight = poleCoverComp.heightFt
  }

  const crates = crateGroups.map((g, i) => buildCrate(
    i + 1,
    g.components,
    g.signHeightFt,
    { requiresEngineeredSplit: g.requiresEngineeredSplit || false }
  ))

  const requiresEngineeredSplit = crates.some(c => c.requiresEngineeredSplit)

  const totalProductWeight = round2(displayComponents.reduce((s, c) => s + c.weight, 0))
  const totalCratingWeight = round2(crates.reduce((s, c) => s + c.cratingWeightLb, 0))
  const totalWeight = round2(crates.reduce((s, c) => s + c.totalWeightLb, 0))

  return {
    crates,
    components: displayComponents.map(c => ({
      type: c.type,
      label: c.label,
      lengthFt: c.lengthFt,
      heightFt: c.heightFt,
      weight: c.weight,
      sections: c.sections
    })),
    emcSections: emcSections.map(s => ({
      label: s.label,
      lengthFt: round2(s.lengthFt),
      heightFt: round2(s.heightFt),
      gridIndex: s.gridIndex
    })),
    overallSignHeight: round2(overallSignHeight),
    totalProductWeight,
    totalCratingWeight,
    totalWeight,
    requiresEngineeredSplit
  }
}

export function edgeQuoteFormToCrateInputs (form, enums = {}) {
  const { EMC_FACES = {} } = enums
  const noEmc = form.noEmc || (form.emcModsH <= 0 && form.emcModsL <= 0)
  const noCabinet = form.noCabinet || (form.cabinetHeightIn <= 0 && form.cabinetLengthIn <= 0)
  const noPoleCover = form.noPedestal || (form.pedestalHeightIn <= 0 && form.pedestalLengthIn <= 0)
  const noMonument = form.noMonument || !form.monumentStyle

  const emc = noEmc ? null : {
    lengthFt: Number(form.emcModsL) || 0,
    heightFt: Number(form.emcModsH) || 0,
    face: form.emcFace === EMC_FACES.SF ? 'Single' : 'Double'
  }

  const cabinet = noCabinet ? null : {
    lengthFt: (Number(form.cabinetLengthIn) || 0) / 12,
    heightFt: (Number(form.cabinetHeightIn) || 0) / 12
  }

  const poleCover = noPoleCover ? null : {
    lengthFt: (Number(form.pedestalLengthIn) || 0) / 12,
    heightFt: (Number(form.pedestalHeightIn) || 0) / 12
  }

  const monument = noMonument ? null : {
    style: form.monumentStyle === 'DuraCore Monument' ? 'DuraCore' : form.monumentStyle,
    lengthFt: (Number(form.monumentLengthIn) || 0) / 12,
    heightFt: (Number(form.monumentHeightIn) || 0) / 12
  }

  return { emc, cabinet, poleCover, monument, arrangement: 'stacked' }
}
